import React, { useState, useEffect } from "react";
import { Search, Plus, Download, Filter } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getSalesList } from "@/redux/Sales/actions";
import FileUpload from "../FileUpload/FileUpload";
import { DataTable } from "../ui/table";
import { getSalesColumns } from "./columns";
import { callLoader } from "@/redux/Loader/actions";
import Pagination from "@/pages/PortPlace/Pagination";
import { useDebounced } from "@/hooks/useDebounced";

const SalesOrderList = ({ onCreateNew, onEditOrder }) => {
  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState("ALL");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [fileUploadVisible, setFileUploadVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const { salesList, pagination } = useSelector((state) => state.sales);
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
  const authName = authUser?.username;
  const companyId = authUser?.companyId;



  const debouncedSearch = useDebounced((value) => {
    _getSalesList(1, value);
  }, 300);
  useEffect(() => {
    if (searchText === null) return;
    debouncedSearch(searchText);
  }, [searchText]);
  useEffect(() => {
    _getSalesList(1);
  }, [pageSize]);

  const _getSalesList = async (page = 1) => {
    const payload = {
      IPageNo: page,
      IPageRecords: pageSize,
      FilterType: searchField,
      SearchKeyword: searchText,
      compnayId:companyId || 0
    };
    dispatch(callLoader(1));
    try {
      await dispatch(getSalesList(payload));
      setCurrentPage(page);
      dispatch(callLoader(0));
    } catch (error) {
      dispatch(callLoader(0));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(salesList.map((order) => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId) => {
    if (selectedOrders.includes(orderId)) {
      setSelectedOrders(selectedOrders.filter((id) => id !== orderId));
    } else {
      setSelectedOrders([...selectedOrders, orderId]);
    }
  };

  const submitNote = async () => {
    if (!noteText) {
      alert("Note cannot be empty!");
      return;
    }

    try {
      // Replace this with your actual API endpoint and request logic
      const response = await fetch(
        `/api/orders/${selectedOrderId.soId}/notes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ note: noteText }),
        },
      );

      if (response.ok) {
        alert("Note added successfully!");
        setNoteModalVisible(false);
        setNoteText("");
      } else {
        alert("Failed to add note!");
      }
    } catch (error) {
      console.error("Error submitting note:", error);
      alert("Error submitting note");
    }
  };

  const handlers = {
    selectedOrders,
    salesList,
    handleSelectAll,
    handleSelectOrder,
    onEditOrder,
    setSelectedOrderId,
    setFileUploadVisible,
    setNoteModalVisible,
  };

  const columns = getSalesColumns(handlers);

  const handlePageSize = (size) => {
    setPageSize(size);
  };

  const changePage = (page) => {
    _getSalesList(page);
  };

  return (
    <div className="min-h-full">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-4 ">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Sales Orders
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {pagination?.totalRecord} total orders
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span className="text-sm font-medium">Export</span>
          </button>
          <button className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all flex items-center space-x-2">
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">Filter</span>
          </button>
          <button
            onClick={onCreateNew}
            className="px-5 py-2 bg-[#0072FF] hover:bg-[#0062DD] text-white rounded-lg font-medium transition-all flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>New Order</span>
          </button>
        </div>
      </div>

      {/* Sleek Search Bar */}
      <div className="bg-white  dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-3 mb-4">
        <div className="flex items-center space-x-2">
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            className="px-3 py-2 text-sm border-0 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
          >
            <option value="ALL">All Fields</option>
            <option value="SON">SalesOrder No. #</option>
            <option value="WARE">Warehouse filter</option>
            <option value="POL">POL filter</option>
            <option value="POD">POD filter</option>
            <option value="SKU">SKU</option>
            <option value="CUST">Customer filter</option>
          </select>
          <div className="relative flex-1 max-w-[400px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by order number, customer, or destination..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border-0 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-[#0072FF] focus:bg-white dark:focus:bg-slate-900 transition-all"
            />
          </div>

        </div>
      </div>

      {/* Professional Data Table */}
      <div className="flex-1 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
        <DataTable
          data={salesList || []}
          columns={columns}
          boxClass="shadow-sm border border-slate-200 dark:border-slate-700"
          trClass={
            "hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
          }
          thClass={
            "px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 white whitespace-nowrap uppercase tracking-wider"
          }
          tdClass=" text-sm  px-2 py-2 border-b border-slate-200 dark:border-slate-700"
          htrClass="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50"
        />
        <Pagination
          className="border-t border-slate-200 dark:border-slate-800 pl-2 pr-16 pt-4 pb-8 mb-0 bg-slate-50 dark:bg-slate-800/50 mt-8"
          currentPage={currentPage}
          recordsPerPage={pageSize}
          setRecordsPerPage={handlePageSize}
          onPrevious={changePage}
          onNext={changePage}
          totalRecord={pagination?.totalRecord}
          handleGoto={changePage}
          title="orders"
        />
      </div>

      {/* Modal for Notes */}
      {noteModalVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-[70%] p-6 rounded-xl shadow-xl animate-fadeIn scale-100">
            <h2 className="text-xl font-semibold mb-4">Add Note</h2>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={5}
              className="w-full p-3 border rounded-lg mb-4"
              placeholder="Write your note here..."
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setNoteModalVisible(false)}
                className="px-4 py-2 bg-gray-400 hover:bg-gray-500 rounded-lg"
              >
                Close
              </button>
              <button
                onClick={submitNote}
                className="px-4 py-2 bg-[#0072FF] hover:bg-[#0062DD] text-white rounded-lg"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {fileUploadVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-[70%] p-6 rounded-xl shadow-xl animate-fadeIn scale-100">
            <div>
              <FileUpload
                orderId={selectedOrderId?.soId}
                orderNum={selectedOrderId?.soNumber}
                type={"sales"}
              />
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setFileUploadVisible(false);
                  setSelectedOrderId(null);
                }}
                className="px-4 py-2 bg-gray-400 hover:bg-gray-500 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrderList;
